import { Database, Collection } from '@nozbe/watermelondb';

import { OrganisationAccountingsDBServiceOptions } from '../../types/dbService';
import {
    OrganisationAccountingsModel,
    OrganisationAccountingsPayload,
    PreparedDataOnOrganisationAccountingsUpdate,
} from '../../types/OrganisationAccountings';
import { UserModel, UserPayload } from '../../types/User';
import {
    addRelatedDataBatch,
    appendPreparedOrganisationAccountings,
    appendPreparedUser,
    logUpdateDBActions,
    prepareUpdateOrganisationAccountings,
    prepareUpdateUserFromPostPayload,
} from './helpers/organisationAccountings/organisationAccountingsUpdate';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';

class OrganisationAccountings {
    private database: Database;
    private collection: Collection<OrganisationAccountingsModel>;
    private table = 'organisation_accountings';
    private options: OrganisationAccountingsDBServiceOptions;
    private validation: OrganisationAccountingsDBServiceOptions['validation'];

    constructor(options: OrganisationAccountingsDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
        this.validation = options.validation;
    }

    get() {
        return this.collection.query();
    }

    async update({
        payload,
        organisationAccountingsId,
        userToUpdate,
    }: {
        payload: OrganisationAccountingsPayload;
        organisationAccountingsId: string;
        userToUpdate?: { payload: UserPayload; id: string };
    }) {
        await this.database.write(async () => {
            const orgElement = await this.collection.find(
                organisationAccountingsId,
            );

            const user = userToUpdate
                ? await this.database.collections
                      .get<UserModel>('users')
                      .find(userToUpdate.id)
                : undefined;

            const preparedOrganisationAccountings =
                prepareUpdateOrganisationAccountings({
                    orgElement,
                    payload,
                });

            const preparedUser =
                user && userToUpdate
                    ? prepareUpdateUserFromPostPayload({
                          user,
                          userPayload: userToUpdate.payload,
                      })
                    : undefined;

            let preparedData: PreparedDataOnOrganisationAccountingsUpdate[] =
                appendPreparedOrganisationAccountings(
                    [],
                    preparedOrganisationAccountings,
                );

            if (preparedUser) {
                preparedData = appendPreparedUser(preparedData, preparedUser);
            }

            validateEntityRecords(
                preparedData.filter((record) => record.record != null),
                this.validation,
            );

            await addRelatedDataBatch(
                preparedData.map((item) => item.record),
                this.database,
            );

            logUpdateDBActions({
                orgElement,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
                userUpdated: preparedUser,
            });
        }, 'update-organisation-accountings');
    }

    async disconnectFromProvider(organisationAccountingsId: string) {
        await this.update({
            organisationAccountingsId,
            payload: {
                accountingProvider: null,
                salesAccountCode: null,
            },
        });
    }
}
export default OrganisationAccountings;
